if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector('[data-rec-submit-default]') ||
          this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.requireModel = this.dataset.requireModel === 'true';
        if (this.requireModel) this.initModelRequirement();
      }

      get productInfo() {
        return this.closest('product-info');
      }

      get modelSelects() {
        return this.productInfo?.querySelector('variant-selects[data-require-model]');
      }

      get selectedModelNode() {
        return this.querySelector('[data-selected-model]');
      }

      initModelRequirement() {
        // One document-level listener keeps the "selected model" line in sync, even after the picker is re-rendered.
        if (!window.__modelRequirementListener) {
          window.__modelRequirementListener = true;
          document.addEventListener('change', (event) => {
            const input = event.target;
            if (!input.matches?.('variant-selects[data-require-model] input[type="radio"]')) return;
            if (!/^model/i.test(input.name)) return;

            window.__modelChosen = true;
            const form = document.querySelector('product-form[data-require-model="true"]');
            form?.showSelectedModel(input.value);
          });
        }

        if (window.__modelChosen) {
          // Picker was re-rendered after the customer chose a model (e.g. product swap).
          if (this.dataset.modelSelectedLabel) this.showSelectedModel(this.dataset.modelSelectedLabel);
          return;
        }

        if (this.modelSelects?.dataset.modelUnselected === 'true') this.setModelUnselected();
      }

      setModelUnselected() {
        const info = this.productInfo;
        info.classList.add('is-model-unselected');

        info
          .querySelectorAll('variant-selects[data-require-model] input[type="radio"]')
          .forEach((input) => {
            if (/^model/i.test(input.name)) input.checked = false;
          });

        const selectedName = info.querySelector('.selected_option-name');
        if (selectedName) selectedName.textContent = '';

        info.querySelectorAll('input[name="id"]').forEach((input) => (input.value = ''));

        if (this.selectedModelNode) this.selectedModelNode.hidden = true;
        this.toggleSubmitButton(false, window.variantStrings?.addToCart);
      }

      showSelectedModel(label) {
        this.productInfo?.classList.remove('is-model-unselected');
        this.modelSelects?.classList.remove('model-required-error');

        const node = this.selectedModelNode;
        if (!node) return;
        node.querySelector('[data-selected-model-value]').textContent = label;
        node.hidden = false;
      }

      showModelRequiredError() {
        this.handleErrorMessage(this.dataset.modelRequiredMessage || 'Please select your model.');

        const selects = this.modelSelects;
        if (!selects) return;
        selects.classList.add('model-required-error');
        selects.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.requireModel && !this.variantIdInput.value) {
          this.showModelRequiredError();
          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

         if (this.dataset.redirectToPdp === 'true' && this.dataset.productUrl) {
          window.location.href = this.dataset.productUrl;
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const isCartDrawerRec = this.classList.contains('cart-drawer__recommendation-form');

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (isCartDrawerRec && window.CartDrawerRecommendations) {
                window.CartDrawerRecommendations.clearPending();
                this.closest('cart-drawer-recommendation')?.setAddedState(false);
              }

              publish(PUB_SUB_EVENTS.cartError, {
                source: isCartDrawerRec ? 'cart-drawer-recommendation' : 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const addedVariantId = formData.get('id');
            const cartUpdateSource = isCartDrawerRec ? 'cart-drawer-recommendation' : 'product-form';

            if (isCartDrawerRec && addedVariantId && window.CartDrawerRecommendations) {
              const recEl = this.closest('cart-drawer-recommendation');
              const cardEl = recEl?.closest('.cart-drawer__recommendation-card');
              const slideEl = recEl?.closest('.swiper-slide');
              const productId = cardEl?.dataset?.productId;
              const variantSnapshot = recEl?.getVariantById?.(addedVariantId);
              const slideIndex = window.CartDrawerRecommendations.getSlideIndexFromElement(slideEl);

              window.CartDrawerRecommendations.queueSuccess(
                addedVariantId,
                productId,
                variantSnapshot,
                slideIndex
              );

              if (recEl) {
                recEl.selectVariantById(addedVariantId, { preserveAdded: true });
                recEl.setAddedState(true);
              }
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: cartUpdateSource,
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response, {
                  fromRecommendation: isCartDrawerRec,
                });
              });
            }
          })
          .catch((e) => {
            if (isCartDrawerRec && window.CartDrawerRecommendations) {
              window.CartDrawerRecommendations.clearPending();
              this.closest('cart-drawer-recommendation')?.setAddedState(false);
            }
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            if (isCartDrawerRec) {
              this.submitButton.classList.remove('loading');
              this.querySelector('.loading__spinner')?.classList.add('hidden');
            } else if (!this.error && !this.submitButton.classList.contains('is-added')) {
              this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner')?.classList.add('hidden');
            } else {
              this.querySelector('.loading__spinner')?.classList.add('hidden');
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (text && String(text).trim() !== '') {
            this.submitButtonText.textContent = text;
          } else {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
